import { ArrowRight, BookOpen, CheckCircle2, Code2, Trophy } from 'lucide-react'
import { Link } from '#router'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useDevice } from '@/hooks/use-mobile'
import Footer from './footer'

export function MobileHome() {
  const { isTablet } = useDevice()

  return (
    <div className="flex flex-col">
      <div className="relative overflow-hidden">
        <div className="px-4 py-8">
          {/* Platform Hero Section */}
          <div className="mb-8 flex flex-col items-center space-y-4 text-center">
            <h1 className="text-3xl leading-tight font-bold tracking-tight">
              <span className="block">Master Interviews</span>
              <span className="block">with</span>
              <span className="text-[#51B0BC] block">Beyond CTCI</span>
            </h1>
            <p className="text-muted-foreground text-base">
              A modern companion platform for{' '}
              <em>Beyond Cracking the Coding Interview</em>. Write, run, and
              test your solutions to every problem so you&rsquo;re prepared for
              the same bar that top candidates are held to.
            </p>
            <div className="flex flex-col items-stretch gap-3 pt-1 w-full max-w-xs">
              <Button asChild size="lg" className="group text-base">
                <Link href="/problems">
                  Start Practicing
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-base">
                <a
                  href="https://www.amazon.com/dp/195570600X"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Buy the Book
                </a>
              </Button>
            </div>
          </div>

          {/* Stats Strip */}
          <div className="mb-8 grid grid-cols-3 gap-2">
            <div className="bg-card rounded-xl border px-3 py-3 text-center">
              <p className="text-[#51B0BC] text-xl font-semibold">26</p>
              <p className="text-muted-foreground text-xs">Topics</p>
            </div>
            <div className="bg-card rounded-xl border px-3 py-3 text-center">
              <p className="text-[#51B0BC] text-xl font-semibold">~300</p>
              <p className="text-muted-foreground text-xs">Problems</p>
            </div>
            <div className="bg-card rounded-xl border px-3 py-3 text-center">
              <p className="text-[#51B0BC] text-xl font-semibold">#1</p>
              <p className="text-muted-foreground text-xs">
                Amazon Best Seller in DS&amp;A
              </p>
            </div>
          </div>

          {/* Features Section */}
          <div className={isTablet ? 'grid grid-cols-3 gap-4' : 'grid gap-4'}>
            <Card>
              <CardHeader className="p-4">
                <div className="bg-primary/10 mb-3 flex h-10 w-10 items-center justify-center rounded-lg">
                  <BookOpen className="text-primary h-5 w-5" />
                </div>
                <CardTitle className="text-base">
                  Structured Curriculum
                </CardTitle>
                <CardDescription className="text-sm">
                  Step-by-step editorials covering data structures, algorithms,
                  and problem-solving strategies.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader className="p-4">
                <div className="bg-primary/10 mb-3 flex h-10 w-10 items-center justify-center rounded-lg">
                  <Code2 className="text-primary h-5 w-5" />
                </div>
                <CardTitle className="text-base">Interview-Focused</CardTitle>
                <CardDescription className="text-sm">
                  Purpose-built to complement the book with practical examples
                  and coding exercises.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader className="p-4">
                <div className="bg-primary/10 mb-3 flex h-10 w-10 items-center justify-center rounded-lg">
                  <Trophy className="text-primary h-5 w-5" />
                </div>
                <CardTitle className="text-base">Author-Maintained</CardTitle>
                <CardDescription className="text-sm">
                  Beyond CTCI content assembled by the authors and continuously
                  updated for current interview trends.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </div>

      {/* Book Hero Section */}
      <div className="bg-muted/40 border-y">
        <div className="px-4 py-8">
          <div className="flex flex-col items-center space-y-6 text-center">
            <p className="text-primary text-sm font-semibold tracking-wide uppercase">
              Official Sequel
            </p>
            <h2 className="text-2xl font-bold tracking-tight">
              The Sequel to the Best-Selling Interview Classic
            </h2>
            <p className="text-muted-foreground text-base">
              For over a decade,{' '}
              <em className="text-[#51B0BC]">Cracking the Coding Interview</em>{' '}
              has been hailed as the &ldquo;bible&rdquo; of interview prep. Now,
              Beyond Cracking the Coding Interview builds on that foundation to
              prepare you for today&rsquo;s tougher technical interviews.
            </p>
            <div className="space-y-3 text-left">
              <div className="text-foreground flex items-start gap-2">
                <CheckCircle2 className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                <span className="text-sm">
                  26 technical topics and ~300 curated coding problems
                </span>
              </div>
              <div className="text-foreground flex items-start gap-2">
                <CheckCircle2 className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                <span className="text-sm">
                  Also use{' '}
                  <a
                    href="https://start.interviewing.io/beyond-ctci/all-problems/technical-topics"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600/90 font-medium underline decoration-sky-500/40 underline-offset-4 transition-colors hover:text-sky-500 hover:decoration-sky-500"
                  >
                    interviewing.io&apos;s free AI interviewer companion
                  </a>{' '}
                  for guided practice
                </span>
              </div>
              <div className="text-foreground flex items-start gap-2">
                <CheckCircle2 className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                <span className="text-sm">
                  Insights from 100k+ interviews and hiring data
                </span>
              </div>
            </div>
            <div className="from-primary/25 to-primary/5 rounded-2xl bg-gradient-to-br p-4 shadow-xl ring-1 ring-white/20">
              <img
                src="/images/book-cover.png"
                alt="Beyond Cracking the Coding Interview book cover"
                width={540}
                height={675}
                className="w-full max-w-[200px] rounded-lg shadow-xl"
              />
            </div>
            <Button asChild size="lg" className="text-base">
              <a
                href="https://www.amazon.com/dp/195570600X"
                target="_blank"
                rel="noopener noreferrer"
              >
                Buy Now
              </a>
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}
